/**
 * Enhancement System
 *
 * Players pay 100g at the Enhancement screen to apply a random enhancement
 * to any inventory item (non-rune).
 *
 * Enhancement level = playerLevel ± 5 (min 1).
 * Number of attributes depends on item rarity:
 *   common 1 · uncommon 2 · rare 2–3 · epic 3–4 · legendary 4
 * Stat values scale linearly with enhancement level.
 * A new enhancement always overwrites the existing one.
 */

// ── Stat pool ─────────────────────────────────────────────────────────────────
// Each entry: key → function(level) → value at that enhancement level.

export const ENHANCEMENT_STAT_POOL = {
  // All values nerfed 50% from original; two new survivability stats added.
  attackDamage:  (l) => parseFloat((0.15 + l * 0.06).toFixed(1)),   // at l10=0.75 l50=3.15
  speedMult:     (l) => parseFloat((0.008 + l * 0.0015).toFixed(3)), // at l10=2.3% l50=8.3%
  rangeMult:     (l) => parseFloat((0.008 + l * 0.0015).toFixed(3)), // same
  cdMult:        (l) => parseFloat((-(0.005 + l * 0.001)).toFixed(3)),// at l10=-1.5% l50=-5.5%
  armor:         (l) => Math.max(1, Math.round(0.25 + l * 0.125)),   // at l10=1 l50=6
  critRate:      (l) => parseFloat((0.005 + l * 0.0005).toFixed(3)), // at l10=1% l50=3%
  critDamage:    (l) => parseFloat((0.02 + l * 0.0035).toFixed(3)),  // at l10=0.055 l50=0.195
  xpPerKill:     (l) => Math.max(1, Math.round(l * 0.06)),           // at l10=1 l50=3
  fireRes:       (l) => parseFloat((0.005 + l * 0.00075).toFixed(3)),// at l10=1.25% l50=4.25%
  lightningRes:  (l) => parseFloat((0.005 + l * 0.00075).toFixed(3)),// same
  maxHp:         (l) => Math.round(3 + l * 0.8),                     // at l10=11 l50=43
  dodgeChance:   (l) => parseFloat((0.003 + l * 0.0006).toFixed(3)), // at l10=0.9% l50=3.3%
};

// How many attributes each rarity receives
const ATTR_COUNTS = {
  common:    () => 1,
  uncommon:  () => 2,
  rare:      () => (Math.random() < 0.5 ? 2 : 3),
  epic:      () => (Math.random() < 0.5 ? 3 : 4),
  legendary: () => 4,
};

/**
 * Roll a new enhancement.
 * @param {number} playerLevel  Persistent player meta-level.
 * @param {string} itemRarity   Rarity of the item being enhanced.
 * @returns {{ level: number, stats: Record<string, number> }}
 */
export function rollEnhancement(playerLevel, itemRarity = 'common') {
  // Level: playerLevel ± 5, clamped to minimum 1
  const offset = Math.floor(Math.random() * 11) - 5;  // -5 … +5
  const level  = Math.max(1, (playerLevel ?? 1) + offset);

  const numAttrs = (ATTR_COUNTS[itemRarity] ?? ATTR_COUNTS.common)();

  // Pick distinct keys at random
  const allKeys  = Object.keys(ENHANCEMENT_STAT_POOL);
  const shuffled = allKeys.slice().sort(() => Math.random() - 0.5);
  const chosen   = shuffled.slice(0, numAttrs);

  const stats = {};
  for (const key of chosen) {
    stats[key] = ENHANCEMENT_STAT_POOL[key](level);
  }

  return { level, stats };
}

/** Human-readable label for each stat key. */
const STAT_LABELS = {
  attackDamage:  (v) => `+${v.toFixed(1)} Attack Damage`,
  speedMult:     (v) => `+${Math.round(v * 100)}% Move Speed`,
  rangeMult:     (v) => `+${Math.round(v * 100)}% Attack Range`,
  cdMult:        (v) => v < 0 ? `${Math.round(Math.abs(v) * 100)}% Faster Attack` : `+${Math.round(v * 100)}% Slower Attack`,
  armor:         (v) => `+${v} Armor`,
  critRate:      (v) => `+${Math.round(v * 100)}% Crit Rate`,
  critDamage:    (v) => `+${Math.round(v * 100)}% Crit Damage`,
  xpPerKill:     (v) => `+${v} XP per Kill`,
  fireRes:       (v) => `+${Math.round(v * 100)}% Fire Resist`,
  lightningRes:  (v) => `+${Math.round(v * 100)}% Lightning Resist`,
  maxHp:         (v) => `+${v} Max HP`,
  dodgeChance:   (v) => `+${Math.round(v * 100)}% Dodge Chance`,
};

/**
 * Format an enhancement object into an array of human-readable strings.
 * @param {{ level: number, stats: object } | null | undefined} enhancement
 * @returns {string[]}
 */
export function formatEnhancement(enhancement) {
  if (!enhancement?.stats) return [];
  return Object.entries(enhancement.stats)
    .filter(([, v]) => v != null)
    .map(([key, v]) => STAT_LABELS[key]?.(v) ?? `${key}: ${v}`);
}

/** Hint string for how many attributes a rarity can roll. */
export function rarityAttrHint(rarity) {
  return {
    common:    '1 attribute',
    uncommon:  '2 attributes',
    rare:      '2–3 attributes',
    epic:      '3–4 attributes',
    legendary: '4 attributes',
  }[rarity] ?? '1 attribute';
}
